import { useSelector } from "react-redux";
import { useCallback, useMemo, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { selectUserId } from "redux/slices/userSlice";
import { usePageFilter } from "hooks/usePageFilters";
import { ServerSideDataGrid } from "common/components/serverSideDataGrid/serverSideDataGrid";
import { Section } from "features/dashboard/components/section";
import { SectionError } from "features/dashboard/components/sectionError";
import { OpenPendingJobsColumns } from "features/dashboard/sections/jobs/table/columns";
import { OpenPendingJobsFilters } from "features/dashboard/sections/jobs/filters/filters";
import { useDefaultJobStatusIds } from "features/dashboard/hooks/useDefaultJobStatusIds";
import Job from "redux/models/job";
import K from "utilities/constants";
import styles from "features/dashboard/sections/jobs/openPendingJobs.module.scss";

const FILTER_PREFIX = "open_pending_jobs";

const SEARCH_COLUMNS = [
  "id",
  "title",
  "jobStatus",
  "recruitingStartDate",
  "currentOpenings",
  "jobLocation",
  "jobDepartment",
  "jobGroup",
  "extAtsId",
];

const getStatusFilter = (statusId, defaultStatusIds) => {
  return statusId ? [statusId] : defaultStatusIds;
};

export const OpenPendingJobs = ({ clientId, recruiterId }) => {
  const userId = useSelector(selectUserId);
  const history = useHistory();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const gridRef = useRef(null);
  const defaultStatusIds = useDefaultJobStatusIds();

  const [search, setSearch] = usePageFilter(`${FILTER_PREFIX}_search`);
  const [jobStatus, setJobStatus] = usePageFilter(`${FILTER_PREFIX}_status`);
  const [jobDepartment, setJobDepartment] = usePageFilter(
    `${FILTER_PREFIX}_department`,
  );

  const statusId = useMemo(() => jobStatus?.value, [jobStatus]);
  const departmentId = useMemo(() => jobDepartment?.value, [jobDepartment]);
  const statusFilter = useMemo(
    () => getStatusFilter(statusId, defaultStatusIds),
    [statusId, defaultStatusIds],
  );

  const getData = useCallback(
    async (params) => {
      if (statusFilter.length === 0) {
        return { data: [], total: 0 };
      }

      try {
        setLoading(true);
        setError(null);
        const { data, total } = await Job.v2.getAllByClient(
          params.clientId,
          params,
          false,
        );

        return {
          data: data || [],
          total: total || 0,
        };
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [statusFilter],
  );

  const getServerSideData = useCallback(
    (options) => {
      return getData({
        ...options,
        clientId,
        recruiterId,
        ...(search && { search, searchIn: SEARCH_COLUMNS }),
        status: statusFilter,
        ...(jobDepartment != null &&
          (departmentId === -1 ? { hasDepartment: false } : { departmentId })),
      });
    },
    [
      getData,
      clientId,
      recruiterId,
      search,
      statusFilter,
      departmentId,
      jobDepartment,
    ],
  );

  const handleRowClicked = useCallback(
    (event) => {
      const jobId = event.data?.id;
      if (jobId && clientId) {
        history.push(`/clients/${clientId}/job/${jobId}`);
      }
    },
    [clientId, history],
  );

  const handleRetry = useCallback(() => {
    setError(null);
    setLoading(true);
    gridRef.current?.api?.refreshServerSide({ purge: true });
  }, []);

  const handleDataFetchFailed = useCallback((err) => {
    setError(err);
  }, []);

  if (!clientId || !recruiterId) {
    return (
      <Section title="My Open & Pending Jobs">
        <div className={styles.openPendingJobs__emptyState}>
          Please select a client and recruiter to view open pending jobs.
        </div>
      </Section>
    );
  }

  return (
    <Section title="My Open & Pending Jobs" contentMinHeight="458px">
      <OpenPendingJobsFilters
        search={search}
        onSearchChange={setSearch}
        jobStatus={jobStatus}
        onJobStatusChange={setJobStatus}
        jobDepartment={jobDepartment}
        onJobDepartmentChange={setJobDepartment}
        clientId={clientId}
        disabled={loading}
      />
      {error ? (
        <SectionError
          onRetry={handleRetry}
          message={error?.message || "Failed to load jobs. Please try again."}
        />
      ) : (
        <ServerSideDataGrid
          ref={gridRef}
          userId={userId}
          configKey={K.AgGridTable.Keys.OpenPendingJobsVisibleColumn}
          defaultColumnConfig={OpenPendingJobsColumns}
          getData={getServerSideData}
          height={410}
          paginationPageSize={7}
          rowClass={styles.openPendingJobs__row}
          onRowClicked={handleRowClicked}
          onDataFetchFailed={handleDataFetchFailed}
          useGlobalLoading={false}
          showTableLoadingOverlay
          useCustomLoader
        />
      )}
    </Section>
  );
};
